// src/features/hotels/components/HotelList.tsx

import { useMemo } from 'react';
import {
  List,
  TextField,
  NumberField,
  FunctionField,
  ReferenceInput,
  useTranslate,
} from 'react-admin';
import { Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import CustomDatagrid from '@/components/common/CustomDatagrid';
import { CustomSelectInputFilter, CustomTextInputFilter } from '@/components/common/CustomFilters';
import { useHotelPermissions } from '../hooks/useHotelPermissions';
import HotelActions from './HotelActions';
import { compactListSx } from '@/components/common/compactListSx';
import { getCurrentUser } from '@/utils/auth';
import { SectionHeader } from '@/components/common/SectionHeader';
import { FilterPopoverProvider, RaFilterPopover } from '@/components/common/RaFilterPopover';
import { hotelsHelpConfig } from '@/config/helpConfig';
import { RegistryLinkedChip } from '@/components/forms/RegistryLinkedChip';

/**
 * Hotels list component
 * Displays all hotels with filtering and sorting capabilities
 */
export const HotelList = ({ embedded }: { embedded?: boolean }) => {
  const translate = useTranslate();
  const { canEdit, canCreate } = useHotelPermissions();
  const navigate = useNavigate();
  const user = getCurrentUser();
  const isSuperAdmin = user?.role === 'SuperAdmin' || user?.role === 'Admin';
  const isGroupAdmin = user?.role === 'GroupAdmin';
  const assignedGroups = user?.assignedGroups ?? [];
  const isMultiGroupAdmin = isGroupAdmin && assignedGroups.length > 1;
  const showGroupFilter = isSuperAdmin || isMultiGroupAdmin;

  // When the group filter is hidden, search is the only filter → inline field.
  // Otherwise search lives in the popup alongside the group filter.
  const searchOnly = !showGroupFilter;

  const popoverChildren = searchOnly ? undefined : (
    <>
      <CustomTextInputFilter
        source="q"
        label={translate('resources.hotels.list.search')}
        alwaysOn
      />
      <ReferenceInput
        source="groupId"
        reference="groups"
        sort={{ field: 'name', order: 'ASC' }}
        filter={isMultiGroupAdmin ? { id: assignedGroups } : {}}
        alwaysOn
      >
        <CustomSelectInputFilter
          label={translate('resources.hotels.fields.groupId')}
          optionText="name"
        />
      </ReferenceInput>
    </>
  );

  const list = (
    <List
      sx={compactListSx}
      actions={false}
      sort={{ field: 'groupName', order: 'ASC' }}
      perPage={25}
      title={translate('resources.hotels.list.title')}
      filterDefaultValues={{}}
      storeKey={isMultiGroupAdmin ? 'hotels-multigroup' : 'hotels'}
      empty={false}
    >
      <RaFilterPopover searchSource="q">{popoverChildren}</RaFilterPopover>
      <CustomDatagrid
        bulkActionButtons={false}
        rowClick={canEdit ? 'edit' : false}
        primarySource="name"
        secondarySource="groupName"
      >

        
    {/* Actions column */}
        <FunctionField
          label=""
          sortable={false}
          render={(record: any) => <HotelActions record={record} />}
        />

        {/* Name */}
        <TextField
          source="name"
          label={translate('resources.hotels.fields.name')}
        />
        
        {/* Group */}
        <TextField 
          source="groupName" 
          label={translate('resources.hotels.fields.groupName')}
          emptyText="-"
        />
        
               
        {/* Registry link */}
        <FunctionField
          label={translate('resources.hotels.fields.registry')}
          sortable={false}
          render={(record: any) => <RegistryLinkedChip record={record} />}
        />

        {/* Device Count */}
        <NumberField 
          source="deviceCount" 
          label={translate('resources.hotels.fields.deviceCount')}
          emptyText="0"
        />

      </CustomDatagrid>
    </List>
  );

  if (embedded) return list;

  return (
    <FilterPopoverProvider>
      <Box sx={{ p: { xs: 1, sm: 1 } }}>
        <SectionHeader
          helpConfig={hotelsHelpConfig}
          useRaFilters
          showSearch={searchOnly}
          searchPlaceholder={translate('resources.hotels.list.search')}
          showCreate={canCreate}
          onCreateClick={() => navigate('/hotels/create')}
        />
        {list}
      </Box>
    </FilterPopoverProvider>
  );
};